<template>
  <BaseModule
    v-bind="$props"
    class="single-room"
    data-module="single-room"
  >
    <div
      v-if="(editing && !accommodationId) || loading"
      class="loader-container"
    >
      <Loader v-if="loading" />
      <div
        v-if="editing && !accommodationId"
        class="non-select-state-message"
      >
        Selecione um quarto ➡
      </div>
    </div>

    <EmptyState
      v-else-if="!selectedRoom"
      :title="$t('notAvailableTitle')"
      :subtitle="$t('notAvailableSubtitle')"
      :img="accommodationsImg"
    />

    <TextAndMedia
      v-else
      v-bind="$props"
      :text-position="textPosition === 'left' ? 'row' : 'row-reverse'"
      :title="selectedRoom.Nome"
      :subtitle="roomSubtitle"
      :text="selectedRoom.Descricao"
      :slides="allSlides"
      :get-button-model-prop="getButtonModel"
    >
      <template #title>
        <component
          :is="titleTag"
          class="tit tit-g"
        >
          {{ selectedRoom.Nome }}
        </component>
      </template>

      <template #swiper>
        <button
          class="botao-abrir-modal"
          @click="openModal(selectedRoom)"
        >
          <IconOpen
            class="text-white"
            :class="{ 'right-side': textPosition === 'right' }"
          />
        </button>
      </template>

      <template #footer>
        <div
          v-if="showValor && selectedRoom.MenorTarifa.Valor"
          class="valor"
        >
          <div class="valor__label small-info">
            {{ $t('dailyRate') }}
            <span
              v-if="selectedRoom.MenorTarifa.TemDesconto"
              class="valor valor-promo"
            >
              {{ selectedRoom.MenorTarifa.ValorFormatadoSemDesconto }}
            </span>
          </div>
          <div
            class="half-content valor__info"
            :class="{ 'has-promo': selectedRoom.MenorTarifa.TemDesconto }"
          >
            <span class="valor">
              {{ selectedRoom.MenorTarifa.ValorFormatado }}
            </span>
          </div>
          <div
            class="half-content valor__disclaimer"
            @mouseover="showTooltip"
            @mouseleave="hideTooltip"
          >
            <span
              ref="slb-tooltip"
              class="slb-tooltip"
            >
              {{ getDisclaimer(selectedRoom) }}
            </span>
            <span class="info-icon"></span>
          </div>
        </div>
      </template>
    </TextAndMedia>

    <template slot="modal">
      <div
        v-show="isModalOpen"
        class="modal-single-room"
      >
        <button
          class="modal-single-room__close"
          @click="closeModal"
        >
          <h2
            v-if="selectedRoom"
            class="room-name"
          >
            {{ selectedRoom.Nome }}
          </h2>
          <IconClose />
        </button>

        <div class="custom-scrollbar">
          <div class="modal-single-room__content">
            <div class="modal-single-room__content__amenities">
              <ul
                v-if="selectedRoom"
                class="list-amenities"
              >
                <li
                  v-for="(am, index) in selectedRoom.Amenidades"
                  :key="index"
                  class="list-amenities__item"
                >
                  {{ am }}
                </li>
              </ul>
            </div>
            <div class="modal-single-room__content__photos">
              <ul
                v-if="selectedRoom"
                class="list-photos"
              >
                <li
                  v-for="(im, index) in selectedRoom.Imagens.Galeria"
                  :key="index"
                  class="bg list-photos__item"
                  :style="{ backgroundImage: `url('${im}')` }"
                ></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseModule>
</template>

<script>
import { mapGetters } from 'vuex';

import base from '@/mixins/base';
import getApiHotels from '@/mixins/getApiHotels';
import IconClose from '@/components/icons/IconClose.vue';
import IconOpen from '@/components/icons/IconOpen.vue';
import Loader from '@/components/Loader.vue';
import EmptyState from './EmptyState.vue';
import BaseModule from './generic/BaseModule.vue';
import TextAndMedia from './generic/TextAndMedia.vue';

const emptyAccommodationsUrlPath = require('@/assets/empty-accommodations.png');

export default {
  name: 'SingleRoom',
  components: {
    Loader,
    EmptyState,
    BaseModule,
    TextAndMedia,
    IconClose,
    IconOpen,
  },
  mixins: [getApiHotels, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    ...TextAndMedia.props,
    hotelId: {
      type: Number,
      default: null,
    },
    accommodationId: {
      type: Number,
      default: null,
    },
    showValor: {
      type: Boolean,
      default: true,
    },
    textPosition: {
      type: String,
      default: 'left', // overrides TextAndMedia default
    },
    buttons: {
      type: Array,
      default: () => [
        {
          buttonText: 'Reservar',
          buttonUseRoomLink: true,
          buttonUrl: {
            type: 'externalLink',
            value: '',
            openInNewTab: true,
          },
          buttonFeatured: true,
        },
      ],
    },
  },
  data() {
    return {
      accommodations: [],
      accommodationsImg: emptyAccommodationsUrlPath,
      isModalOpen: null,
      loading: false,
    };
  },
  computed: {
    editing() {
      return this.$parent === undefined;
    },
    allSlides() {
      const roomGallery = [];

      if (this.selectedRoom?.Imagens?.Galeria) {
        this.selectedRoom.Imagens.Galeria.forEach((image) => {
          roomGallery.push({
            image,
            imageMobile: image,
          });
        });
      }

      return roomGallery;
    },
    selectedRoom() {
      return this.accommodations.find(
        (acc) => acc.Codigo === this.accommodationId,
      );
    },
    roomSubtitle() {
      let subtitle = `${this.$t('max')}: ${this.$tc(
        'adult',
        this.selectedRoom.MaximoAdultos,
      )}`;

      if (this.selectedRoom.MaximoCriancas) {
        subtitle = subtitle.concat(
          ` ${this.$t('and')} ${this.$tc(
            'child',
            this.selectedRoom.MaximoCriancas,
          )}`,
        );
      }

      return subtitle;
    },
    ...mapGetters(['settings']),
  },
  watch: {
    hotelId(val) {
      if (typeof val === 'number') {
        this.$store.commit('SET_ACTIVE_EDITABLE_OBJECT_DATA', {
          path: 'data.accommodationId',
          value: null,
        });
        this.accommodations = [];
        this.requestAccommodations();
      }
    },
  },
  mounted() {
    this.getApiHotels();

    if (this.hotelId) {
      this.requestAccommodations();
    }
  },
  methods: {
    async requestAccommodations() {
      this.loading = true;

      const urlAccommodations = `${this.settings.letsbookApiId}/acomodacoes/${this.hotelId}/${this.apiLocale}`;
      if (!this.apiData[urlAccommodations]) {
        try {
          await this.$store.dispatch('loadApiData', urlAccommodations);
        } catch {
          return;
        }
      }
      this.loading = false;
      this.accommodations = this.apiData[urlAccommodations];
    },
    getDisclaimer(room) {
      const adultos = room.MenorTarifa.NumeroAdultos;
      const noites = room.MenorTarifa.NumeroNoites;

      return this.$t('disclaimer', {
        nights: this.$tc('night', noites),
        adults: this.$tc('adult', adultos),
      });
    },
    getButtonModel({ buttonUseRoomLink, buttonUrl }) {
      return buttonUseRoomLink && this.selectedRoom
        ? this.selectedRoom.MenorTarifa.LinkPublico ||
            this.settings.letsbookUrl.replace(/\/admin\/?$/i, '/D/')
        : buttonUrl;
    },
    openModal() {
      this.isModalOpen = true;
      document.querySelector('html').classList.add('scroll-block');
    },
    closeModal() {
      this.isModalOpen = null;
      document.querySelector('html').classList.remove('scroll-block');
    },
    showTooltip(event) {
      const el = event.target;
      const parentPosition = el.parentElement.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      if (parentPosition.bottom <= viewportHeight - 200) {
        this.$refs['slb-tooltip'].classList.add('open-down');
      } else {
        this.$refs['slb-tooltip'].classList.add('open-up');
      }
    },
    hideTooltip() {
      this.$refs['slb-tooltip'].classList.remove('open-up', 'open-down');
    },
  },
  metadata: {
    extends: [BaseModule.metadata, TextAndMedia.metadata],
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'config',
            position: 0,
            fields: [
              {
                name: 'hotelId',
                position: 0,
                type: 'VueSelect',
                label: 'Selecione o hotel',
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Selecione um hotel',
                  },
                },
              },
              {
                name: 'accommodationId',
                after: 'hotelId',
                type: 'VueSelect',
                label: 'Selecione o quarto',
                options: {
                  watch: true,
                  attribute: 'accommodations',
                  handler(accommodations) {
                    const accommodationsOptions = accommodations.map((acc) => ({
                      label: acc.Nome,
                      value: acc.Codigo,
                    }));
                    return accommodationsOptions;
                  },
                },
                isEnabled({ hotelId }) {
                  return hotelId;
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Selecione um quarto',
                  },
                },
              },
              {
                name: 'textPosition', // just updates options values
                position: 2,
                type: 'VueSelect',
                label: 'Posição do texto',
                options: [
                  {
                    label: 'Esquerda',
                    value: 'left',
                  },
                  {
                    label: 'Direita',
                    value: 'right',
                  },
                ],
              },
              {
                name: 'showValor',
                position: 3,
                type: 'Checkbox',
                label: 'Mostrar preços dos quartos',
                default: true,
              },
            ],
          },
          {
            name: 'buttons',
            label: 'Botões',
            fields: [
              {
                name: 'buttons',
                fields: [
                  {
                    name: 'buttonUseRoomLink',
                    before: 'buttonUrl',
                    type: 'Checkbox',
                    label: 'Usar link do Letsbook',
                  },
                  {
                    name: 'buttonUrl',
                    isEnabled() {
                      return !this.buttonUseRoomLink;
                    },
                  },
                ],
              },
            ],
          },
        ],
        toRemove: ['text', 'gallery'],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        dailyRate: 'Hospedagem a partir de',
        max: 'Máximo',
        adult: '1 adulto | {count} adultos',
        child: '1 criança | {count} crianças',
        and: 'e',
        night: '1 diária | {count} diárias',
        disclaimer:
          'Valor da diária baseado em uma pesquisa de {nights} para {adults}',
        notAvailableTitle: 'Ops, não encontramos nenhum quarto disponível.',
        notAvailableSubtitle:
          'Em breve listaremos aqui todas as nossas acomodações para você escolher a que mais se encaixa no seu perfil.',
      },
      en: {
        dailyRate: 'Room rates from',
        max: 'Maximum',
        adult: '1 adult | {count} adults',
        child: '1 child | {count} children',
        and: 'and',
        night: '1-night | {count}-nights',
        disclaimer: 'Room rates based on a {nights} stay for {adults}',
        notAvailableTitle: 'Ops, we did not find any available room.',
        notAvailableSubtitle:
          "We'll soon list all our rooms here so that you can find the one that best suits your profile.",
      },
      es: {
        dailyRate: 'Tarifas a partir de',
        max: 'Máximo',
        adult: '1 adulto | {count} adultos',
        child: '1 niño | {count} niños',
        and: 'y',
        night: '1 noche | {count} noches',
        disclaimer:
          'Valor de la diaria basado en una busca de {nights} para {adults}',
        notAvailableTitle: 'Ops, no encontramos habitaciones disponibles.',
        notAvailableSubtitle:
          'En breve enumeraremos aquí todos nuestros alojamientos para que elijas el que mejor se adapta a tu perfil.',
      },
    },
  },
};
</script>

<style scoped lang="scss">
.single-room {
  .loader-container {
    width: 100%;
    text-align: center;
    padding: 96px 20px;

    .loader + .non-select-state-message {
      margin-top: 30px;
    }

    .non-select-state-message {
      font-size: 20px;
      font-weight: $lb-semibold;
      font-family: $lb-setting-font;
    }
  }

  .empty-state ::v-deep .content {
    background: transparent;
  }

  .valor {
    min-width: 200px;
    margin-right: 12px;
    padding: 4px 0;

    &__label {
      text-transform: uppercase;
      font-size: $size-normal;

      .valor-promo {
        display: block;
        color: $lb-gray;
        margin-bottom: 0.2em;
        text-decoration: line-through;
      }
    }

    &__info {
      font-weight: $lb-bold;
      text-transform: none;
      color: var(--primary-color);

      .valor {
        font-size: 26px;
        color: inherit;
      }
    }

    .has-promo {
      color: var(--promo-color);
    }

    &__disclaimer {
      margin-left: 15px;
      position: relative;

      .slb-tooltip {
        right: -10px;
        visibility: hidden;
        opacity: 0;
        color: $lb-dark-gray;

        &.open-up,
        &.open-down {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
      }
    }
  }

  .half-content {
    display: inline-block;
    vertical-align: middle;
  }

  .info-icon {
    line-height: 1em;
  }

  .botao-abrir-modal {
    border: none;
    position: absolute;
    border: 0;
    background: none;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 2;
    cursor: pointer;

    svg {
      box-sizing: content-box;
      background-color: rgba($lb-black, 0.5);
      position: absolute;
      left: 0;
      bottom: 0;
      width: 30px;
      height: 30px;
      padding: 5px;

      @include media-breakpoint-up(sm) {
        width: 35px;
        height: 35px;
        padding: 9px;
      }

      &.right-side {
        @include media-breakpoint-up(sm) {
          left: unset;
          right: 0;
        }
      }
    }
  }

  .modal-single-room {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 9999;
    text-align: center;
    background: $lb-white;

    .custom-scrollbar {
      height: auto;
      margin: 0;
      max-height: calc(100vh - 65px);
    }

    .room-name {
      font-size: 40px;
      font-weight: $lb-bold;
      color: $black;
    }

    &__close {
      text-align: center;
      width: 100%;
      background: none;
      border: 0;
      height: 65px;
      cursor: pointer;
      text-align: center;
      outline: inherit;
      max-width: 1240px;
      position: relative;

      .room-name {
        display: inline-block;
        font-size: 20px;
        line-height: 50px;
        transition: opacity 0.2s;
        text-align: left;
        max-width: 1240px;
        width: 100%;
      }

      .icon {
        width: 25px;
        margin: 15px;
        position: absolute;
        right: 0;
        top: 0;
        z-index: 999;
      }
    }

    &__content {
      width: 100%;
      max-width: 1240px;
      padding-right: 15px;
      display: inline-block;

      &__amenities {
        text-align: left;
        display: inline-block;
        width: 250px;
        vertical-align: top;
        padding: 0 12px;

        @include media-breakpoint-down(lg) {
          width: 100%;
          margin-bottom: 20px;
        }

        .list-amenities {
          &__item {
            display: block;
            font-size: $size-normal;
            color: $black;
            font-weight: $lb-semibold;
            margin-bottom: 10px;

            @include media-breakpoint-down(lg) {
              display: inline-block;
              font-size: $size-small;
            }

            &:not(:first-child) {
              &::before {
                @include media-breakpoint-down(lg) {
                  content: '.';
                  font-size: 18px;
                  content: '\00B7';
                  display: inline-block;
                  margin: 0 7px;
                }
              }
            }
          }
        }
      }

      &__photos {
        display: inline-block;
        width: calc(100% - 260px);
        vertical-align: top;

        @include media-breakpoint-down(lg) {
          width: 100%;
        }

        .list-photos {
          &__item {
            width: 100%;
            height: 461px;
            margin-bottom: 10px;
          }
        }
      }
    }
  }
}
</style>
